import React, { useMemo, useState } from 'react';

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const formatDate = (d) => {
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const createItem = () => ({ id: `${Date.now()}-${Math.random()}`, text: '' });
const createCategory = (name = '') => ({ id: `${Date.now()}-${Math.random()}`, name, items: [createItem()] });

const DEFAULT_CATEGORIES = [
  'Welcome Refreshments',
  'Soups',
  'Vegetarian Starters',
  'Non-Vegetarian Starter',
  'Main Course (Vegetarian)',
  'Main Course (Non-Vegetarian)',
  'Desserts',
  'After Meal',
].map((name) => createCategory(name));

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid var(--iocl-border)',
  background: 'var(--iocl-panel)',
  color: 'var(--iocl-white)',
};

const labelStyle = { display: 'block', fontSize: 12, marginBottom: 6, color: 'var(--iocl-muted)' };

export default function Quotation() {
  const [eventName, setEventName] = useState('Safety Meeting – MPSO Bhopal');
  const [eventDate, setEventDate] = useState(today());
  const [guests, setGuests] = useState('');
  const [ratePerPlate, setRatePerPlate] = useState('');
  const [gstIncluded, setGstIncluded] = useState(true);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [closingNote, setClosingNote] = useState(
    'We sincerely appreciate the opportunity to present this quotation for your esteemed event. We look forward to serving {event} with exceptional food quality, professional service, and a memorable dining experience.'
  );
  const [tagline, setTagline] = useState('Excellence in Every Bite | Professional Catering Services');

  const totalAmount = useMemo(() => (Number(ratePerPlate) || 0) * (Number(guests) || 0), [ratePerPlate, guests]);

  const setCategoryName = (categoryId, name) => {
    setCategories((rows) => rows.map((c) => (c.id === categoryId ? { ...c, name } : c)));
  };
  const addCategory = () => setCategories((rows) => [...rows, createCategory()]);
  const removeCategory = (categoryId) => setCategories((rows) => rows.filter((c) => c.id !== categoryId));

  const setItemText = (categoryId, itemId, text) => {
    setCategories((rows) =>
      rows.map((c) => (c.id === categoryId ? { ...c, items: c.items.map((it) => (it.id === itemId ? { ...it, text } : it)) } : c))
    );
  };
  const addItem = (categoryId) => {
    setCategories((rows) => rows.map((c) => (c.id === categoryId ? { ...c, items: [...c.items, createItem()] } : c)));
  };
  const removeItem = (categoryId, itemId) => {
    setCategories((rows) =>
      rows.map((c) => (c.id === categoryId ? { ...c, items: c.items.filter((it) => it.id !== itemId) } : c))
    );
  };

  const closingNoteResolved = closingNote.replace('{event}', eventName || 'your event');

  return (
    <div className="page-body">
      <div className="cq-print print-only">
        <div className="cq-print-banner">
          <div className="cq-print-banner-title">Catering Quotation</div>
          <div className="cq-print-banner-subtitle">Professional Event Catering Services</div>
        </div>

        <div className="cq-print-section">
          <div className="cq-print-heading">Event Details</div>
          <table className="cq-print-table">
            <tbody>
              <tr>
                <td className="cq-print-label">Event</td>
                <td>{eventName}</td>
              </tr>
              <tr>
                <td className="cq-print-label">Date</td>
                <td>{formatDate(eventDate)}</td>
              </tr>
              <tr>
                <td className="cq-print-label">Number of Guests</td>
                <td>{guests || 0} Pax</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="cq-print-section">
          <div className="cq-print-heading">Menu</div>
          {categories.filter((c) => c.name.trim() || c.items.some((it) => it.text.trim())).map((c) => (
            <div className="cq-print-category" key={c.id}>
              <div className="cq-print-category-name">{c.name}</div>
              <ul className="cq-print-item-list">
                {c.items.filter((it) => it.text.trim()).map((it) => (
                  <li key={it.id}>{it.text}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="cq-print-section">
          <div className="cq-print-heading">Commercial Details</div>
          <table className="cq-print-table cq-print-commercial">
            <thead>
              <tr>
                <th>Particulars</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Rate Per Plate</td>
                <td>{fmt(ratePerPlate)}{gstIncluded ? ' (GST Included)' : ''}</td>
              </tr>
              <tr>
                <td>Number of Guests</td>
                <td>{guests || 0} Pax</td>
              </tr>
              <tr className="cq-print-total-row">
                <td>Total Amount</td>
                <td>{fmt(totalAmount)}{gstIncluded ? ' (GST Included)' : ''}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="cq-print-closing">{closingNoteResolved}</div>

        <div className="cq-print-signature">
          <div>Warm Regards,</div>
          <div className="cq-print-signature-name">{contactName}</div>
          <div className="cq-print-signature-phone">{contactPhone}</div>
        </div>

        <div className="cq-print-tagline">{tagline}</div>
      </div>

      <div className="quotation-screen-header screen-only" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'Rajdhani', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Quotation Builder</div>
          <div className="subtitle">Create professional catering quotations for events and approvals.</div>
        </div>
        <button className="btn btn-success btn-sm" onClick={() => window.print()}>
          Print Quotation
        </button>
      </div>

      <div className="card screen-only" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 14 }}>Event Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Event Name</label>
            <input type="text" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="Safety Meeting – MPSO Bhopal" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Number of Guests</label>
            <input type="number" min="0" value={guests} onChange={(e) => setGuests(e.target.value)} placeholder="40" style={inputStyle} />
          </div>
        </div>
      </div>

      <div className="card screen-only" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontWeight: 700 }}>Menu</div>
          <button className="btn btn-primary btn-sm" type="button" onClick={addCategory}>
            + Add category
          </button>
        </div>

        {categories.map((c) => (
          <div key={c.id} style={{ marginBottom: 18, padding: 14, borderRadius: 10, border: '1px solid var(--iocl-border)' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
              <input
                type="text"
                value={c.name}
                onChange={(e) => setCategoryName(c.id, e.target.value)}
                placeholder="Category name (e.g. Soups)"
                style={{ ...inputStyle, fontWeight: 700 }}
              />
              <button className="btn btn-danger btn-sm" type="button" onClick={() => removeCategory(c.id)} disabled={categories.length === 1}>
                ✕
              </button>
            </div>

            {c.items.map((it) => (
              <div key={it.id} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center', paddingLeft: 16 }}>
                <input
                  type="text"
                  value={it.text}
                  onChange={(e) => setItemText(c.id, it.id, e.target.value)}
                  placeholder="Item name"
                  style={inputStyle}
                />
                <button className="btn btn-danger btn-sm" type="button" onClick={() => removeItem(c.id, it.id)} disabled={c.items.length === 1}>
                  ✕
                </button>
              </div>
            ))}
            <div style={{ paddingLeft: 16, marginTop: 6 }}>
              <button className="btn btn-secondary btn-sm" type="button" onClick={() => addItem(c.id)}>
                + Add item
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card screen-only" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 14 }}>Commercial Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>Rate Per Plate (₹)</label>
            <input type="number" min="0" value={ratePerPlate} onChange={(e) => setRatePerPlate(e.target.value)} placeholder="1200" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>
              <input type="checkbox" checked={gstIncluded} onChange={(e) => setGstIncluded(e.target.checked)} style={{ marginRight: 6 }} />
              GST Included
            </label>
          </div>
          <div>
            <label style={labelStyle}>Total Amount</label>
            <div style={{ ...inputStyle, fontWeight: 700 }}>{fmt(totalAmount)}</div>
          </div>
        </div>
      </div>

      <div className="card screen-only" style={{ padding: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 14 }}>Closing Note &amp; Signature</div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Closing Note (use {'{event}'} to insert the event name)</label>
          <textarea
            value={closingNote}
            onChange={(e) => setClosingNote(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Contact Name</label>
            <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Your name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Contact Phone</label>
            <input type="text" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Phone number" style={inputStyle} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Footer Tagline</label>
          <input type="text" value={tagline} onChange={(e) => setTagline(e.target.value)} style={inputStyle} />
        </div>
      </div>
    </div>
  );
}
